import { debounce } from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Modals } from '@/helpers/modals';
import {
  type EMissionBonusGroupKey,
  MISSION_LOTBOXES_GROUP_KEYS,
} from '@/modules/promotions/mission/helpers';
import { useMissionPrize } from '@/modules/promotions/mission/hooks/useMissionPrize';
import { STORAGE, useLocalStorageStore, useModalsStore } from '@/store';

export const useMissionModal = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const open = useModalsStore.use.open();
  const modal = searchParams.get('modal');

  const { get, replace } = useLocalStorageStore(
    useShallow((state) => ({
      get: state.get,
      replace: state.replace,
    }))
  );

  const ids = useRef(get<number[]>(STORAGE.MISSION) || []);

  const prize = useMissionPrize();

  const [debouncedPrize, setDebouncedPrize] = useState(prize);

  useEffect(() => {
    const debouncedSetPrize = debounce(() => {
      setDebouncedPrize(prize);
    }, 3000);

    debouncedSetPrize();

    return () => {
      debouncedSetPrize.cancel();
    };
  }, [prize]);

  useEffect(() => {
    const isGamePage = pathname.startsWith('/game/');

    if (!modal && debouncedPrize && !isGamePage) {
      const isPopupShown = ids.current?.includes(debouncedPrize.id);
      const groupKey = debouncedPrize.groupKey as EMissionBonusGroupKey;
      const shouldSkip = MISSION_LOTBOXES_GROUP_KEYS.includes(groupKey);

      if (isPopupShown || shouldSkip) return;

      replace(STORAGE.MISSION, [...ids.current, debouncedPrize.id]);
      ids.current = [...ids.current, debouncedPrize.id];
      open(Modals.MissionComplete);
    }
  }, [pathname, modal, debouncedPrize, open, replace]);
};
